import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AgentBookingModal.css';

const AgentBookingModal = ({ booking, isOpen, onClose, onApprove, onReject, loading }) => {
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Helper function to ensure document URLs are correctly formed
  const getAbsoluteDocumentUrl = (documentUrl) => {
    if (!documentUrl) return '';
    
    console.log("Processing document URL:", documentUrl);
    
    // If already absolute URL with http/https, return as is
    if (documentUrl.startsWith('http://') || documentUrl.startsWith('https://')) {
      // If the URL has localhost, but we're not on localhost, update it
      if (documentUrl.includes('localhost') && window.location.hostname !== 'localhost') {
        const currentOrigin = window.location.origin;
        // Extract the path portion (after the domain)
        const urlParts = documentUrl.split('/');
        // Find the uploads part in the URL
        const uploadsIndex = urlParts.findIndex(part => part === 'uploads');
        
        if (uploadsIndex >= 0) {
          const newUrl = `${currentOrigin}/${urlParts.slice(uploadsIndex).join('/')}`;
          console.log("Converted localhost URL to:", newUrl);
          return newUrl;
        }
      }
      return documentUrl;
    }
    
    // If it's a relative URL starting with /
    if (documentUrl.startsWith('/')) {
      const absoluteUrl = `${window.location.origin}${documentUrl}`;
      console.log("Converted relative URL to absolute:", absoluteUrl);
      return absoluteUrl;
    }
    
    // If it's a relative URL without leading slash
    const absoluteUrl = `${window.location.origin}/${documentUrl}`;
    console.log("Added origin to URL:", absoluteUrl);
    return absoluteUrl;
  };

  // Function to fetch documents for a booking
  const fetchBookingDocuments = async (bookingId) => {
    try {
      setLoadingDocuments(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Try to get documents from the dedicated API endpoint
      try {
        const docsResponse = await axios.get(`/api/uploads/documents/${bookingId}`, config);
        
        if (docsResponse.data && Array.isArray(docsResponse.data) && docsResponse.data.length > 0) {
          console.log('Successfully loaded documents from API:', docsResponse.data);
          setDocuments(docsResponse.data);
          return;
        } else {
          console.log('No documents found from API endpoint for booking:', bookingId);
        }
      } catch (apiErr) {
        console.error('Error loading documents from API:', apiErr);
      }
      
      // Fallback: Get the booking details and extract document info manually
      const response = await axios.get(`/api/bookings/${bookingId}`, config);
      const bookingData = response.data;
      
      console.log('Checking for documents in booking object:', bookingData);
      
      // Collect all document types from the booking
      const documentsList = [];
      const baseUrl = window.location.origin;
      
      // Helper function to format URL
      const formatUrl = (url) => {
        if (!url) return null;
        return url.startsWith('http') 
          ? url 
          : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      };
      
      // Check for standard document
      if (bookingData.documentUrl) {
        console.log('Standard document found:', bookingData.documentUrl);
        documentsList.push({
          documentUrl: formatUrl(bookingData.documentUrl),
          documentType: 'uploaded_document',
          documentName: bookingData.documentOriginalName || 'Uploaded Document'
        });
      }
      
      // Check for cheque document
      if (bookingData.chequeUrl) {
        console.log('Cheque document found:', bookingData.chequeUrl);
        documentsList.push({
          documentUrl: formatUrl(bookingData.chequeUrl),
          documentType: 'cheque',
          documentName: bookingData.chequeOriginalName || 'Payment Cheque'
        });
      }
      
      // Check for IC front document
      if (bookingData.icFrontUrl) {
        console.log('IC Front document found:', bookingData.icFrontUrl);
        documentsList.push({
          documentUrl: formatUrl(bookingData.icFrontUrl),
          documentType: 'ic_front',
          documentName: bookingData.icFrontOriginalName || 'IC Front'
        });
      }
      
      // Check for IC back document
      if (bookingData.icBackUrl) {
        console.log('IC Back document found:', bookingData.icBackUrl);
        documentsList.push({
          documentUrl: formatUrl(bookingData.icBackUrl),
          documentType: 'ic_back',
          documentName: bookingData.icBackOriginalName || 'IC Back'
        });
      }
      
      console.log('Final document list:', documentsList);
      setDocuments(documentsList);
    } catch (err) {
      console.error('Error loading booking documents:', err);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Function to download a document
  const downloadDocument = (documentUrl, documentName) => {
    if (!documentUrl) {
      console.error('No document URL provided');
      return;
    }
    
    const formattedUrl = getAbsoluteDocumentUrl(documentUrl);
    console.log('Downloading document from URL:', formattedUrl);
    
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = formattedUrl;
    link.download = documentName || 'document';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to view document in new tab
  const viewDocument = (documentUrl) => {
    if (!documentUrl) {
      console.error('No document URL provided');
      return;
    }
    
    const formattedUrl = getAbsoluteDocumentUrl(documentUrl);
    console.log('Opening document URL:', formattedUrl);
    window.open(formattedUrl, '_blank');
  };

  // Format date in a readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get property information with fallbacks
  const getPropertyInfo = (request) => {
    if (request.lot) {
      return {
        type: request.lot.type || 'Lot',
        number: request.lot.lotNumber || request.lot.number || 'N/A'
      };
    }
    
    if (request.unit) {
      return {
        type: request.unit.type || 'Unit',
        number: request.unit.unitNumber || request.unit.number || 'N/A'
      };
    }
    
    return {
      type: request.lotType || request.propertyType || request.type || 'Property',
      number: request.lotNumber || request.propertyNumber || request.number || 'N/A'
    };
  };

  // Get client information with fallbacks
  const getClientInfo = (request) => {
    if (request.user) {
      return {
        name: request.user.name || 'Unknown Client',
        email: request.user.email || 'N/A',
        phone: request.user.phone || request.contactPhone || 'N/A'
      };
    }
    
    return {
      name: request.name || request.clientName || request.userName || 'Unknown Client',
      email: request.email || request.clientEmail || request.userEmail || 'N/A',
      phone: request.phone || request.contactPhone || request.clientPhone || 'N/A'
    };
  };

  // Fetch documents when modal opens
  useEffect(() => {
    if (isOpen && booking && booking._id) {
      fetchBookingDocuments(booking._id);
    }
  }, [isOpen, booking]);

  if (!isOpen || !booking) return null;

  const propertyInfo = getPropertyInfo(booking);
  const clientInfo = getClientInfo(booking);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content agent-booking-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Booking Request Details</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {/* Property Information */}
          <div className="detail-section">
            <h4>Property Information</h4>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Property Type:</span>
                <span className="detail-value">{propertyInfo.type}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Property Number:</span>
                <span className="detail-value">{propertyInfo.number}</span>
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="detail-section">
            <h4>Client Information</h4>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{clientInfo.name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{clientInfo.email}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{clientInfo.phone}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Date Submitted:</span>
                <span className="detail-value">{formatDate(booking.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Request Details */}
          <div className="detail-section">
            <h4>Request Details</h4>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`status-badge status-${booking.status}`}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace(/_/g, ' ')}
                </span>
              </div>
              {booking.description && (
                <div className="detail-item full-width">
                  <span className="detail-label">Description:</span>
                  <div className="detail-text">{booking.description}</div>
                </div>
              )}
            </div>
          </div>

          {/* Documents Section */}
          <div className="detail-section">
            <h4>Uploaded Documents</h4>
            {loadingDocuments ? (
              <div className="loading-indicator">Loading documents...</div>
            ) : documents.length > 0 ? (
              <div className="documents-list">
                {/* Group documents by type */}
                {documents.some(doc => doc.documentType === 'cheque') && (
                  <div className="document-group">
                    <h5>Payment Cheque</h5>
                    {documents
                      .filter(doc => doc.documentType === 'cheque')
                      .map((doc, index) => (
                        <div key={`cheque-${index}`} className="document-item">
                          <span className="document-name">{doc.documentName}</span>
                          <div className="document-actions">
                            <button 
                              className="btn-view-document"
                              onClick={() => viewDocument(doc.documentUrl)}
                            >
                              View
                            </button>
                            <button 
                              className="btn-download-document"
                              onClick={() => downloadDocument(doc.documentUrl, doc.documentName)}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
                
                {documents.some(doc => doc.documentType === 'ic_front' || doc.documentType === 'ic_back') && (
                  <div className="document-group">
                    <h5>Identity Card</h5>
                    {documents
                      .filter(doc => doc.documentType === 'ic_front' || doc.documentType === 'ic_back')
                      .map((doc, index) => (
                        <div key={`ic-${index}`} className="document-item">
                          <span className="document-type">
                            {doc.documentType === 'ic_front' ? 'Front' : 'Back'}
                          </span>
                          <span className="document-name">{doc.documentName}</span>
                          <div className="document-actions">
                            <button 
                              className="btn-view-document"
                              onClick={() => viewDocument(doc.documentUrl)}
                            >
                              View
                            </button>
                            <button 
                              className="btn-download-document"
                              onClick={() => downloadDocument(doc.documentUrl, doc.documentName)}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
                
                {/* Other documents */}
                {documents.some(doc => doc.documentType !== 'cheque' && doc.documentType !== 'ic_front' && doc.documentType !== 'ic_back') && (
                  <div className="document-group">
                    <h5>Other Documents</h5>
                    {documents
                      .filter(doc => doc.documentType !== 'cheque' && doc.documentType !== 'ic_front' && doc.documentType !== 'ic_back')
                      .map((doc, index) => (
                        <div key={`other-${index}`} className="document-item">
                          <span className="document-name">{doc.documentName}</span>
                          <div className="document-actions">
                            <button 
                              className="btn-view-document"
                              onClick={() => viewDocument(doc.documentUrl)}
                            >
                              View
                            </button>
                            <button 
                              className="btn-download-document"
                              onClick={() => downloadDocument(doc.documentUrl, doc.documentName)}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            ) : (
              <div className="no-documents">No documents uploaded with this booking.</div>
            )}
          </div>

          {/* Response Information (if not pending) */}
          {booking.status !== 'pending_agent' && (
            <div className="detail-section">
              <h4>Response Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Response Date:</span>
                  <span className="detail-value">{formatDate(booking.agentResponseDate)}</span>
                </div>
                <div className="detail-item full-width">
                  <span className="detail-label">Agent Notes:</span>
                  <div className="detail-text">{booking.agentNotes || 'No notes provided'}</div>
                </div>
                {booking.respondedBy && (
                  <div className="detail-item">
                    <span className="detail-label">Responded By:</span>
                    <span className="detail-value">
                      {booking.respondedBy.name || booking.respondedBy.email || 'Unknown User'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          {booking.status === 'pending_agent' ? (
            <>
              <button
                className="btn-approve" 
                onClick={() => {
                  onApprove(booking._id);
                  onClose();
                }}
                disabled={loading}
              >
                Approve
              </button>
              <button 
                className="btn-reject" 
                onClick={() => {
                  onReject(booking._id);
                  onClose();
                }}
                disabled={loading}
              >
                Reject
              </button>
            </>
          ) : (
            <button 
              className="btn-close" 
              onClick={onClose}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentBookingModal; 